import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Snowflake } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

const QUICK = [
  { role: "Admin", email: "admin@chericious.com", pwd: "admin123" },
  { role: "Prod Manager", email: "prod@chericious.com", pwd: "prod123" },
  { role: "Supervisor", email: "super@chericious.com", pwd: "super123" },
  { role: "Operator", email: "op@chericious.com", pwd: "op123" },
  { role: "QC", email: "qc@chericious.com", pwd: "qc123" },
  { role: "Store", email: "store@chericious.com", pwd: "store123" },
  { role: "Purchase", email: "purchase@chericious.com", pwd: "pur123" },
  { role: "Sales", email: "sales@chericious.com", pwd: "sales123" },
  { role: "Dispatch", email: "dispatch@chericious.com", pwd: "disp123" },
];

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("admin@chericious.com");
  const [password, setPassword] = useState("admin123");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e?.preventDefault?.();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background">
      {/* Left brand panel */}
      <div className="hidden md:flex flex-col justify-between p-12 bg-[#1a1a1a] text-white relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-16">
            <div className="w-11 h-11 rounded-md bg-primary flex items-center justify-center">
              <Snowflake weight="fill" size={24} />
            </div>
            <div>
              <div className="font-display font-black tracking-tight text-lg">CHERICIOUS</div>
              <div className="overline text-white/60">Cloud ERP · v1.0</div>
            </div>
          </div>
          <h1 className="font-display font-black text-4xl lg:text-5xl leading-[1.05] tracking-tighter mb-6">
            Freeze-dried excellence,<br/>
            <span className="text-primary">managed end-to-end.</span>
          </h1>
          <p className="text-white/70 text-base max-w-md leading-relaxed">
            Every batch traceable from vegetable procurement to freeze-dry cycle to finished pouch.
            Built for the Chericious factory floor.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-6 relative z-10">
          {[
            ["150 kg", "Small Dryer / 24h"],
            ["300 kg", "Big Dryer / 24h"],
            ["40+", "Active SKUs"],
          ].map(([v, l]) => (
            <div key={l}>
              <div className="text-metric text-2xl text-primary">{v}</div>
              <div className="overline text-white/60 mt-1">{l}</div>
            </div>
          ))}
        </div>
        <div className="absolute -bottom-40 -right-40 w-96 h-96 rounded-full bg-primary/20 blur-3xl" />
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="overline mb-2">Sign in</div>
            <h2 className="font-display font-black text-3xl tracking-tighter">Welcome to Chericious ERP</h2>
            <p className="text-sm text-muted-foreground mt-2">Enter your factory credentials to continue.</p>
          </div>
          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label className="overline mb-1.5 block">Email</Label>
              <Input
                type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                placeholder="you@chericious.com"
              />
            </div>
            <div>
              <Label className="overline mb-1.5 block">Password</Label>
              <Input
                type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
              />
            </div>
            <Button type="submit" disabled={loading} className="w-full" data-testid="login-submit">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="mt-8 border-t border-border pt-6">
            <div className="overline mb-3">Quick sign-in (demo)</div>
            <div className="grid grid-cols-3 gap-2">
              {QUICK.map((q) => (
                <button
                  key={q.email}
                  type="button"
                  onClick={() => { setEmail(q.email); setPassword(q.pwd); }}
                  className="text-[11px] font-semibold px-2 py-1.5 rounded-md border border-border hover:border-primary hover:text-primary transition-colors"
                  data-testid={`quick-${q.role.toLowerCase().replace(/ /g, "-")}`}
                >
                  {q.role}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
